/**
 * Public stats page — /stats/:token (season) and /stats/:token/game/:gameId.
 * 2026-10-17 (Press Box). Anonymous, read only, no app shell.
 *
 * Shows what the coach chose (jersey numbers, initials, or first names —
 * never full names) and is marked noindex. While a game is live the page
 * refreshes itself every 20 seconds; on the day of a game, every minute until
 * it starts. A refresh that fails keeps what is on screen (one dropped
 * request at the rink used to replace the page with an error for good).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import { StatTable, GOALIE_COLS } from '../../components/stats/StatTable.jsx';
import StatsExplorer from '../../components/stats/StatsExplorer.jsx';

// 2026-10-19 (round 5, per Gilles) — the game box score, NHL style: every
// stat the pass produced, one column each, for every player on the sheet.
// 2026-10-22 (per Gilles) - the position letter (F forward, D defense,
// G goalie) rides beside the name, muted.
export const withPos = (r) => (<>{r.label}{r.pos ? <span style={{ color: 'var(--text-3)', fontWeight: 500, marginLeft: 6, fontSize: '0.85em' }} title={{ F: 'Forward', D: 'Defense', G: 'Goalie' }[r.pos] || r.pos}>{r.pos}</span> : null}</>);
const GAME_SKATER_COLS = [
  { key: 'label', label: 'Player', left: true, bold: true, render: withPos },
  { key: 'g', label: 'G', title: 'Goals' },
  { key: 'a', label: 'A', title: 'Assists' },
  { key: 'p', label: 'P', title: 'Points', bold: true },
  { key: 'sog', label: 'SOG', title: 'Shots on goal' },
  { key: 'ms', label: 'MS', title: 'Missed shots', render: (r) => r.ms || '' },
  { key: 'bs', label: 'BS', title: 'Blocked shots', render: (r) => r.bs || '' },
  { key: 'fo', label: 'FO', title: 'Faceoffs won–lost', render: (r) => ((r.fow || 0) + (r.fol || 0) > 0 ? `${r.fow || 0}–${r.fol || 0}` : '') },
  { key: 'foPct', label: 'FO%', title: 'Faceoff win percentage', render: (r) => { const t = (r.fow || 0) + (r.fol || 0); return t > 0 ? `${Math.round(((r.fow || 0) / t) * 100)}%` : ''; } },
  { key: 'pim', label: 'PIM', title: 'Penalty minutes', render: (r) => r.pim || '' },
];
import { fmtGameTime } from '../../utils/pressBoxFormat.js';

function useNoIndex(title) {
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'robots'; m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    const prev = document.title;
    if (title) document.title = title;
    return () => { m.remove(); document.title = prev; };
  }, [title]);
}

const fmtDate = (d) => {
  if (!d) return '';
  const [y, mo, day] = d.split('-').map(Number);
  return new Date(y, mo - 1, day).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};
const mmss = (sec) => { if (sec == null) return ''; const s0 = Math.round(sec); return `${Math.floor(s0 / 60)}:${String(s0 % 60).padStart(2, '0')}`; };
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Load `url`, keep the last good answer when a refresh fails (only a 404 —
 * the link was turned off or replaced — clears the page), and refresh every
 * `everyMs(data)` milliseconds (0 = not now).
 */
function usePublicData(url, params, notFound, everyMs) {
  const [data, setData] = useState(null);
  const [stale, setStale] = useState(false);
  const key = JSON.stringify(params || {});
  const load = useCallback(async () => {
    try {
      const { data: d } = await publicApi.get(url, { params: JSON.parse(key) });
      setData(d); setStale(false);
    } catch (e) {
      const gone = e.response?.status === 404;
      setData(prev => (prev && !prev.error && !gone) ? prev : { error: gone ? notFound : 'Could not load the stats. Check your connection and reload.' });
      setStale(!gone);
    }
  }, [url, key, notFound]);
  useEffect(() => { load(); }, [load]);
  const every = data && !data.error ? everyMs(data) : 0;
  useEffect(() => {
    if (!every) return undefined;
    const t = setInterval(load, every);
    return () => clearInterval(t);
  }, [every, load]);
  return { data, stale };
}

const StaleNote = ({ show }) => (show
  ? <div className="text-xs mb-3" style={{ color: 'var(--text-3)' }}>Couldn't refresh just now — trying again.</div>
  : null);

export function Frame({ children, team, sub, logo }) {
  // 2026-10-18 — the team's logo, when one was uploaded (hidden if the
  // request fails, so a missing file never shows a broken image).
  const [logoOk, setLogoOk] = useState(true);
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'var(--font-body)' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 16px 40px' }}>
        <div className="mb-4 flex items-center gap-3">
          {logo && logoOk && (
            <img src={logo} alt="" aria-hidden="true" onError={() => setLogoOk(false)}
              style={{ width: 52, height: 52, borderRadius: 12, objectFit: 'cover', border: '1px solid var(--border)', flexShrink: 0 }} />
          )}
          <div>
            <div className="text-xs" style={{ color: 'var(--text-3)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Team stats</div>
            <h1 className="section-title" style={{ fontSize: 26, marginTop: 4 }}>{team || '…'}</h1>
            {sub && <div className="text-sm mt-1" style={{ color: 'var(--text-2)' }}>{sub}</div>}
          </div>
        </div>
        {children}
        <div className="text-xs mt-8" style={{ color: 'var(--text-3)', lineHeight: 1.6 }}>
          Stats come from the team's game narration and are checked after each game; live numbers can change until then.
          Kept with <a href="https://www.game-day.app" style={{ color: 'var(--ice)' }}>Game Day</a>.
        </div>
      </div>
    </div>
  );
}

// 2026-10-18 — the logo endpoint (token-gated, no auth).
const logoUrl = (token, team) => (team?.logo ? `/api/public/stats/${token}/logo` : null);

function Tag({ g }) {
  if (g.live) return <span className="badge" style={{ background: 'var(--penalty-a10)', color: 'var(--penalty)', border: '1px solid var(--penalty-a20)' }}>● Live</span>;
  if (g.official) return <span className="badge" style={{ background: 'var(--goal-a10)', color: 'var(--goal)', border: '1px solid var(--goal-a20)' }}>Final</span>;
  return null;
}

export default function PublicStatsPage() {
  const { token } = useParams();
  const [season, setSeason] = useState('');
  const { data, stale } = usePublicData(`/stats/${token}`, season ? { season } : {}, 'This stats page is not available.',
    (d) => {
      if ((d.games || []).some(g => g.live)) return 20000;
      const today = localToday();
      return (d.games || []).some(g => g.date === today && !g.official) ? 60000 : 0;
    });
  useNoIndex(data?.team?.name ? `${data.team.name} — stats` : 'Team stats');

  if (!data) return <Frame><div style={{ color: 'var(--text-2)' }}>Loading…</div></Frame>;
  if (data.error) return <Frame team="Team stats"><div className="card text-center p-4">{data.error}</div></Frame>;
  const upcoming = data.games.filter(g => (data.upcoming || []).includes(g.id));
  // 2026-10-21 — the same StatsExplorer the coach's Stats tab uses: NHL
  // tables, fuzzy search, sortable columns, player drill-down (published
  // games only, no narrator quotes, labels per the coach's privacy choice).
  const seasonOptions = (data.seasons || []).map(x => ({ id: x.id, name: x.name, active: x.name === data.season }));
  return (
    <Frame team={data.team.name} sub={data.season} logo={logoUrl(token, data.team)}>
      <StaleNote show={stale} />
      <StatsExplorer hiddenCols={data.hiddenCols || []}
        teamName={data.team.name}
        record={data.record}
        games={data.games}
        skaters={data.skaters || []}
        goalies={data.goalies || []}
        updatedAt={data.statsUpdatedAt}
        timeFormat={data.team.timeFormat}
        seasons={seasonOptions.length > 1 ? seasonOptions : null}
        season={season || seasonOptions.find(x => x.active)?.id || 'all'}
        onSeason={(v) => setSeason(v === 'all' ? '' : v)}
        fetchBreakdown={async (row) => {
          const key = row.label === 'Unknown' ? 'unknown' : (row.number != null ? `num:${row.number}` : null);
          if (!key) return null;
          const { data: b } = await publicApi.get(`/stats/${token}/players/breakdown`, { params: { player: key, ...(season ? { season } : {}) } });
          return b;
        }}
        gameHref={(g) => `/stats/${token}/game/${g.id}`}
        canSeeQuotes={false}
        exportName={`${(data.team.name || 'team').replace(/\s+/g, '_').toLowerCase()}_stats`}
      />

      {upcoming.length > 0 && (
        <div className="card mb-4">
          <div className="font-bold text-sm mb-2">Coming up</div>
          {upcoming.map(g => <div key={g.id} className="text-sm" style={{ color: 'var(--text-2)' }}>{fmtDate(g.date)} · {fmtGameTime(g.time, data.team.timeFormat)} · vs {g.opponent || 'TBD'}</div>)}
        </div>
      )}
    </Frame>
  );
}

export function PublicStatsGamePage() {
  const { token, gameId } = useParams();
  const { data, stale } = usePublicData(`/stats/${token}/games/${gameId}`, null, 'This game is not available.',
    (d) => (d.live ? 20000 : (d.game?.date === localToday() && !d.official ? 60000 : 0)));
  useNoIndex(data?.team?.name ? `${data.team.name} vs ${data.game?.opponent || ''}` : 'Game');

  if (!data) return <Frame><div style={{ color: 'var(--text-2)' }}>Loading…</div></Frame>;
  if (data.error) return <Frame team="Team stats"><div className="card text-center p-4">{data.error}</div><Link to={`/stats/${token}`} style={{ color: 'var(--ice)' }}>← All games</Link></Frame>;
  return (
    <Frame team={data.team.name} logo={logoUrl(token, data.team)}>
      <StaleNote show={stale} />
      <Link to={`/stats/${token}`} className="text-sm" style={{ color: 'var(--ice)', textDecoration: 'none' }}>← All games</Link>
      <PublicGameBody data={data} />
      <PublicHighlights token={token} gameId={gameId} />
    </Frame>
  );
}

/**
 * 2026-10-28 (per Gilles) — the goal clips + highlight reel for the
 * families, when the coach turned it on (Settings) and the game is
 * published. Quiet when off or none: the section simply is not there.
 */
function PublicHighlights({ token, gameId }) {
  const [clips, setClips] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    let dead = false;
    publicApi.get(`/stats/${token}/games/${gameId}/clips`)
      .then(({ data: d }) => { if (!dead) setClips(d?.clips || []); })
      .catch(() => { if (!dead) setClips([]); });
    return () => { dead = true; };
  }, [token, gameId]);
  if (!clips || clips.length === 0) return null;
  const src = (id) => `/api/public/stats/${token}/games/${gameId}/clips/${id}/media`;
  const reel = clips.filter(c => c.kind === 'reel');
  const goals = clips.filter(c => c.kind !== 'reel');
  return (
    <div className="card mb-4" data-public-highlights>
      <div className="font-bold text-sm mb-2">▶ Highlights</div>
      {[...reel, ...goals].map(c => (
        <div key={c.id} className="py-1.5 text-sm" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
            <span style={{ fontWeight: c.kind === 'reel' ? 800 : 600,
              color: c.kind === 'goal_against' ? 'var(--penalty)' : (c.kind === 'reel' ? 'var(--gold, #d4a017)' : 'var(--goal)') }}>{c.label}</span>
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>{c.durationMs ? `${Math.round(c.durationMs / 1000)} s` : ''}</span>
            <button type="button" className="btn-ghost" style={{ marginLeft: 'auto', padding: '4px 10px' }}
              onClick={() => setOpen(open === c.id ? null : c.id)}>{open === c.id ? 'Hide' : 'Play'}</button>
          </div>
          {open === c.id && (
            <video src={src(c.id)} controls autoPlay playsInline preload="none"
              style={{ width: '100%', maxHeight: 420, borderRadius: 10, background: '#000', marginTop: 8 }} />
          )}
        </div>
      ))}
    </div>
  );
}

/** One game's cards alone — the public page AND the coach's draft preview. */
export function PublicGameBody({ data }) {
  const g = data.game;
  return (
    <>
      <div className="card mt-3 mb-4 flex items-center justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <div>
          <div className="text-xs" style={{ color: 'var(--text-3)' }}>{fmtDate(g.date)} · {fmtGameTime(g.time, data.team.timeFormat)}</div>
          <div className="font-bold text-lg">vs {g.opponent || 'TBD'}</div>
          <div className="mt-1"><Tag g={{ live: data.live, official: data.official }} /></div>
        </div>
        <div className="display-num" style={{ fontSize: 44 }}>{data.score ? `${data.score.us}–${data.score.them}` : '–'}</div>
      </div>

      {data.summary && (
        <div className="card mb-4 text-sm" style={{ color: 'var(--text-2)', lineHeight: 1.6 }} data-game-summary>
          {data.summary}
        </div>
      )}

      {/* 2026-10-19 — the box score first: every player, every stat, one
          column each (what a parent came for). */}
      <div className="card mb-4" data-box-score>
        <div className="font-bold text-sm mb-2">Player stats</div>
        <StatTable cols={GAME_SKATER_COLS.filter(c => c.key === 'label' || !(data.hiddenCols || []).includes(c.key))} rows={data.players} empty="No player stats for this game." />
      </div>
      {data.goalies.length > 0 && (
        <div className="card mb-4">
          <div className="font-bold text-sm mb-2">Goalie</div>
          <StatTable cols={GOALIE_COLS.filter(c => c.key !== 'gp' && (c.key === 'label' || !(data.hiddenCols || []).includes(c.key)))} rows={data.goalies} />
        </div>
      )}

      {/* scoring summary, NHL style: by period, with time, scorer and assists */}
      <div className="card mb-4" style={{ padding: 0, overflow: 'hidden' }} data-scoring-summary>
        <div className="px-3 py-2 font-bold text-sm" style={{ borderBottom: '1px solid var(--border)' }}>Scoring</div>
        {data.goals.length === 0 && <div className="p-3 text-sm" style={{ color: 'var(--text-3)' }}>No goals yet.</div>}
        {(() => {
          const byP = [];
          for (const x of data.goals) {
            const k = x.period || 0;
            if (!byP.length || byP[byP.length - 1].period !== k) byP.push({ period: k, goals: [] });
            byP[byP.length - 1].goals.push(x);
          }
          return byP.map((grp, gi) => (
            <div key={gi}>
              {grp.period > 0 && (
                <div className="px-3 py-1 text-xs font-bold" style={{ color: 'var(--text-3)', background: 'var(--surface-2)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Period {grp.period}
                </div>
              )}
              {grp.goals.map((x, i) => (
                <div key={i} className="flex items-baseline gap-3 px-3 py-1.5 text-sm" style={{ borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)', minWidth: 42 }}>{mmss(x.t)}</span>
                  <span className="flex-1" style={{ color: x.side === 'us' ? 'var(--text)' : 'var(--text-2)' }}>
                    {x.side === 'us' ? <><strong>{x.scorer}</strong>{x.assists?.length ? ` · assists: ${x.assists.join(', ')}` : ' · unassisted'}</> : `${g.opponent || 'Opponent'} goal`}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)' }}>{x.score}</span>
                </div>
              ))}
            </div>
          ));
        })()}
      </div>

      {data.penalties.length > 0 && (
        <div className="card mb-4">
          <div className="font-bold text-sm mb-2">Penalties</div>
          {data.penalties.map((p, i) => (
            <div key={i} className="text-sm py-1" style={{ color: 'var(--text-2)' }}>{p.period ? `P${p.period} · ` : ''}{p.side === 'us' ? (p.player || 'Our team') : (g.opponent || 'Opponent')}</div>
          ))}
        </div>
      )}

      {/* the AI's game notes — the colour a parent loves */}
      {data.notes?.length > 0 && (
        <div className="card mb-4" data-game-notes>
          <div className="font-bold text-sm mb-2">Game notes</div>
          {data.notes.map((n, i) => (
            <div key={i} className="flex items-baseline gap-3 py-1.5 text-sm" style={{ borderBottom: i < data.notes.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)', minWidth: 42 }}>{n.t != null ? mmss(n.t) : ''}</span>
              <span style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>{n.text}</span>
            </div>
          ))}
        </div>
      )}
      {data.plays?.length > 0 && (
        <div className="card mb-4" style={{ padding: 0, overflow: 'hidden' }} data-play-by-play>
          <div className="px-3 py-2 font-bold text-sm" style={{ borderBottom: '1px solid var(--border)' }}>Play-by-play</div>
          {(() => {
            // 2026-10-18 — the full timeline, grouped by period (the same
            // lines the WhatsApp updates carry; the server already stripped
            // anything beyond the privacy setting).
            const groups = [];
            for (const p of data.plays) {
              const k = p.period || 0;
              if (!groups.length || groups[groups.length - 1].period !== k) groups.push({ period: k, plays: [] });
              groups[groups.length - 1].plays.push(p);
            }
            const playColor = (p) => (p.type === 'goal' ? 'var(--goal)' : p.type === 'goal_against' ? 'var(--penalty)' : 'var(--text)');
            return groups.map((grp, gi) => (
              <div key={gi}>
                {grp.period > 0 && (
                  <div className="px-3 py-1 text-xs font-bold" style={{ color: 'var(--text-3)', background: 'var(--surface-2)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Period {grp.period}
                  </div>
                )}
                {grp.plays.map((p, i) => (
                  <div key={i} className="flex items-baseline gap-3 px-3 py-1.5 text-sm" style={{ borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)', minWidth: 42 }}>{mmss(p.t)}</span>
                    <span style={{ fontWeight: p.type === 'goal' || p.type === 'goal_against' ? 800 : 500, color: playColor(p) }}>{p.label}</span>
                    <span className="flex-1" style={{ color: 'var(--text-2)' }}>
                      {p.side === 'us'
                        ? <>{p.scorer || p.player || ''}{p.assists?.length ? ` · from ${p.assists.join(', ')}` : ''}</>
                        : (g.opponent || 'Opponent')}
                    </span>
                    {p.score && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)' }}>{p.score}</span>}
                  </div>
                ))}
              </div>
            ));
          })()}
          <div className="px-3 py-2 text-xs flex items-center gap-3" style={{ color: 'var(--text-3)', flexWrap: 'wrap' }}>
            <span>{data.plays.length} plays{data.live ? ' so far' : ''} · tape times {mmss(data.plays[0].t)}–{mmss(data.plays[data.plays.length - 1].t)}</span>
            {data.pbpDownload && (
              <button type="button" className="btn-ghost" style={{ padding: '4px 10px', marginLeft: 'auto' }} data-pbp-download
                onClick={() => {
                  // 2026-10-19 — the play-by-play as a text file (never the
                  // transcript: that stays with the team's manager).
                  const line = (p) => [mmss(p.t), p.period ? `P${p.period}` : '', p.label,
                    p.side === 'us' ? (p.scorer || p.player || '') : (g.opponent || 'Opponent'),
                    p.assists?.length ? `assists: ${p.assists.join(', ')}` : '', p.score || ''].filter(Boolean).join('  ');
                  const head = `${data.team.name} vs ${g.opponent || 'TBD'} — ${g.date}${data.score ? ` — final ${data.score.us}–${data.score.them}` : ''}`;
                  const blob = new Blob([head + '\n\n' + data.plays.map(line).join('\n') + '\n'], { type: 'text/plain' });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `play-by-play-${g.date || 'game'}.txt`;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
                }}>
                Download play-by-play
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
